import Link from "next/link";
import { useRouter } from "next/router";
import BrandMark from "~/components/BrandMark";
import ContactButtons from "~/components/ContactButtons";
import { NAV_LABELS } from "~/content/Labels";
import { ROUTES } from "~/content/Routes";
import {
  SERVICE_NAV_GROUPS,
  type ServiceNavGroup,
} from "~/content/serviceNavigation";

const FOOTER_SERVICE_GROUPS: ServiceNavGroup[] = SERVICE_NAV_GROUPS;

const FOOTER_UTILITY_LINKS = [
  { href: ROUTES.SOLUTIONS, label: NAV_LABELS.SOLUTIONS },
  { href: ROUTES.BLOG, label: NAV_LABELS.BLOG },
  { href: ROUTES.CITIES, label: NAV_LABELS.CITIES },
  { href: ROUTES.FAQ, label: NAV_LABELS.FAQ },
  { href: ROUTES.ABOUT_US, label: NAV_LABELS.ABOUT_US },
  { href: ROUTES.CONTACT_US, label: NAV_LABELS.CONTACT_US },
  { href: ROUTES.LEGISLATION, label: NAV_LABELS.LEGISLATION },
  { href: ROUTES.PRIVACY_POLICY, label: NAV_LABELS.PRIVACY_POLICY },
];

const normalizePath = (value: string): string => {
  if (!value) return "/";
  const [path] = value.split(/[?#]/);
  return path === "/" ? path : path.replace(/\/$/, "");
};

export const Footer: React.FC = () => {
  const router = useRouter();
  const currentYear = new Date().getFullYear();
  const currentPath = normalizePath(router.asPath);

  return (
    <footer className="bg-black text-gray-200">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col gap-6 border-b border-white/10 pb-8 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-md">
            <BrandMark inverse showDescriptor />
            <p className="mt-4 text-sm leading-6 text-white/65">
              Platforma do umawiania przeglądów i inspekcji bezpieczeństwa
              budynków. Elektryka, gaz, wentylacja i ppoż w jednym miejscu.
            </p>
          </div>
          <div className="lg:flex-shrink-0">
            <ContactButtons showBookingButton={false} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-4">
          {FOOTER_SERVICE_GROUPS.map(({ heading, links }) => (
            <section className="min-w-0" key={heading}>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-gray-100">
                {heading}
              </h3>
              <ul className="space-y-2.5 text-sm">
                {links.map(({ href, label }) => {
                  const isActive = currentPath === normalizePath(href);
                  return (
                    <li key={href}>
                      <Link
                        href={href}
                        className={
                          isActive
                            ? "text-sky-300 transition-colors"
                            : "text-gray-400 transition-colors hover:text-sky-300"
                        }
                      >
                        {label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>

        <div className="mt-10 border-t border-white/10 pt-6">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-center text-sm text-gray-400 sm:grid-cols-3 lg:grid-cols-7">
            {FOOTER_UTILITY_LINKS.map(({ href, label }) => {
              const isActive = currentPath === normalizePath(href);
              return (
                <Link
                  key={href}
                  href={href}
                  className={
                    isActive
                      ? "text-sky-300 transition-colors"
                      : "transition-colors hover:text-sky-300"
                  }
                >
                  {label}
                </Link>
              );
            })}
          </div>

          <div className="mt-8 flex flex-col gap-2 text-sm text-gray-500 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <BrandMark compact inverse />
              <p>&copy; {currentYear}</p>
            </div>
            <p>
              Developed by{" "}
              <Link
                className="text-gray-300 transition-colors hover:text-sky-300"
                target="_blank"
                href="https://strivelab.pl"
                rel="noreferrer"
              >
                StriveLab.pl
              </Link>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
